import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useI18n } from "../../i18n/I18nContext";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function toDatetimeLocal(value) {
  if (!value) return "";
  return value.replace(" ", "T").slice(0, 16);
}

function fromDatetimeLocal(value) {
  if (!value) return null;
  return value.replace("T", " ") + ":00";
}

export default function AdminCoupons() {
  const { t } = useI18n();
  const token = localStorage.getItem("token");

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
  }, [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [q, setQ] = useState("");

  const [form, setForm] = useState({
    code: "",
    description: "",
    discount_type: "percent",
    discount_value: "",
    min_order_amount: "",
    starts_at: "",
    expires_at: "",
    active: true,
  });

  const [editing, setEditing] = useState(null);

  const fetchCoupons = async () => {
    setErrorMsg("");
    setLoading(true);

    try {
      const res = await api.get(`/api/admin/coupons`, {
        params: q ? { q } : {},
      });

      setItems(res.data?.data || []);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro ao carregar cupões."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async (e) => {
    e.preventDefault();
    await fetchCoupons();
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSaving(true);

    try {
      await api.post(`/api/admin/coupons`, {
        code: form.code,
        description: form.description || null,
        discount_type: form.discount_type,
        discount_value: form.discount_value,
        min_order_amount: form.min_order_amount || null,
        starts_at: fromDatetimeLocal(form.starts_at),
        expires_at: fromDatetimeLocal(form.expires_at),
        active: form.active,
      });

      setForm({
        code: "",
        description: "",
        discount_type: "percent",
        discount_value: "",
        min_order_amount: "",
        starts_at: "",
        expires_at: "",
        active: true,
      });

      await fetchCoupons();
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          t("Erro ao criar cupão. Verifica se o código é único.")
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (coupon) => {
    setEditing({
      id: coupon.id,
      code: coupon.code,
      description: coupon.description || "",
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value ?? "",
      min_order_amount: coupon.min_order_amount ?? "",
      starts_at: toDatetimeLocal(coupon.starts_at),
      expires_at: toDatetimeLocal(coupon.expires_at),
      active: !!coupon.active,
    });
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;

    setErrorMsg("");
    setSaving(true);

    try {
      await api.put(`/api/admin/coupons/${editing.id}`, {
        code: editing.code,
        description: editing.description || null,
        discount_type: editing.discount_type,
        discount_value: editing.discount_value,
        min_order_amount: editing.min_order_amount || null,
        starts_at: fromDatetimeLocal(editing.starts_at),
        expires_at: fromDatetimeLocal(editing.expires_at),
        active: editing.active,
      });

      setEditing(null);
      await fetchCoupons();
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          t("Erro ao atualizar cupão. Verifica se o código é único.")
      );
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (coupon) => {
    const ok = window.confirm(`${t("Apagar o cupão")} "${coupon.code}"?`);
    if (!ok) return;

    setErrorMsg("");

    try {
      await api.delete(`/api/admin/coupons/${coupon.id}`);
      await fetchCoupons();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro ao apagar cupão."));
    }
  };

  const badgeClasses = (coupon) => {
    if (!coupon.active) return "bg-gray-100 text-gray-700";
    if (coupon.is_currently_valid) return "bg-green-100 text-green-700";
    return "bg-yellow-100 text-yellow-700";
  };

  const badgeLabel = (coupon) => {
    if (!coupon.active) return t("Inativo");
    if (coupon.is_currently_valid) return t("Ativo");
    return t("Agendado / Expirado");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("Cupões")}</h1>
            <p className="text-sm text-gray-600">
              {t("Criar, editar e remover cupões de desconto.")}
            </p>
          </div>

          <form onSubmit={onSearch} className="flex gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("Pesquisar por código ou descrição…")}
              className="w-full sm:w-80 rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
            />
            <button
              type="submit"
              className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
            >
              {t("Pesquisar")}
            </button>
          </form>
      </div>

      {errorMsg && (
        <div className="mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      <div className="mt-6 rounded-lg border bg-white p-4 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">{t("Criar cupão")}</h2>

        <form onSubmit={onCreate} className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Código")}
            </label>
            <input
              value={form.code}
              onChange={(e) =>
                setForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
              placeholder={t("EX: WELCOME10")}
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Tipo de desconto")}
            </label>
            <select
              value={form.discount_type}
              onChange={(e) =>
                setForm((s) => ({ ...s, discount_type: e.target.value }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
            >
              <option value="percent">{t("Percentagem")}</option>
              <option value="fixed">{t("Valor fixo")}</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Valor do desconto")}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.discount_value}
              onChange={(e) =>
                setForm((s) => ({ ...s, discount_value: e.target.value }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
              placeholder="10.00"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Compra mínima")}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.min_order_amount}
              onChange={(e) =>
                setForm((s) => ({ ...s, min_order_amount: e.target.value }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
              placeholder={t("Opcional")}
            />
          </div>

          <div className="md:col-span-2 xl:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Descrição")}
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((s) => ({ ...s, description: e.target.value }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
              rows={3}
              placeholder={t("Descrição interna ou visível no backoffice")}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Início")}
            </label>
            <input
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) =>
                setForm((s) => ({ ...s, starts_at: e.target.value }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Expiração")}
            </label>
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) =>
                setForm((s) => ({ ...s, expires_at: e.target.value }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
            />
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  setForm((s) => ({ ...s, active: e.target.checked }))
                }
              />
              {t("Cupão ativo")}
            </label>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {saving ? t("A guardar…") : t("Criar")}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="border-b px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            {t("Lista de cupões")}
          </h2>
        </div>

        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-600">{t("A carregar…")}</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-600">
            {t("Nenhum cupão encontrado.")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-600">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">{t("Código")}</th>
                  <th className="px-4 py-3">{t("Tipo")}</th>
                  <th className="px-4 py-3">{t("Valor")}</th>
                  <th className="px-4 py-3">{t("Compra mínima")}</th>
                  <th className="px-4 py-3">{t("Período")}</th>
                  <th className="px-4 py-3">{t("Estado")}</th>
                  <th className="px-4 py-3 text-right">{t("Ações")}</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {items.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">{coupon.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {coupon.code}
                      {coupon.description && (
                        <div className="mt-1 text-xs text-gray-500">
                          {coupon.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {coupon.discount_type === "percent" ? t("Percentagem") : t("Fixo")}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {coupon.discount_type === "percent"
                        ? `${coupon.discount_value}%`
                        : `${Number(coupon.discount_value).toFixed(2)} €`}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {coupon.min_order_amount !== null
                        ? `${Number(coupon.min_order_amount).toFixed(2)} €`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="text-xs">
                        <div>{t("Início")}: {coupon.starts_at || "—"}</div>
                        <div>{t("Fim")}: {coupon.expires_at || "—"}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${badgeClasses(
                          coupon
                        )}`}
                      >
                        {badgeLabel(coupon)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => startEdit(coupon)}
                          className="rounded-md border px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                        >
                          {t("Editar")}
                        </button>
                        <button
                          onClick={() => onDelete(coupon)}
                          className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                        >
                          {t("Apagar")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white p-5 shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t("Editar cupão")}</h3>
                <p className="text-sm text-gray-600">ID #{editing.id}</p>
              </div>
              <button
                onClick={cancelEdit}
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Código")}
                </label>
                <input
                  value={editing.code}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, code: e.target.value.toUpperCase() }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Tipo de desconto")}
                </label>
                <select
                  value={editing.discount_type}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, discount_type: e.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                >
                  <option value="percent">{t("Percentagem")}</option>
                  <option value="fixed">{t("Valor fixo")}</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Valor do desconto")}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing.discount_value}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, discount_value: e.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Compra mínima")}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing.min_order_amount}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, min_order_amount: e.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Descrição")}
                </label>
                <textarea
                  value={editing.description}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, description: e.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                  rows={3}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Início")}
                </label>
                <input
                  type="datetime-local"
                  value={editing.starts_at}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, starts_at: e.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Expiração")}
                </label>
                <input
                  type="datetime-local"
                  value={editing.expires_at}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, expires_at: e.target.value }))
                  }
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={editing.active}
                    onChange={(e) =>
                      setEditing((s) => ({ ...s, active: e.target.checked }))
                    }
                  />
                  {t("Cupão ativo")}
                </label>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={cancelEdit}
                className="rounded-md border px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {t("Cancelar")}
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
              >
                {saving ? t("A guardar…") : t("Guardar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}